import { useCallback, useMemo } from 'react';
import { dateToApiFormat, getDatePeriod } from '../../helpers/dateHelper';
import { useMapToLeaveTypeId } from '../../services/availability/useAvailabilityTypes';
import { useGetContractName } from '../../services/contract/useGetContractName';
import { useGetDayOfWeekAndHoliday } from '../../services/holiday/usePublicHolidays';
import { useStaffSettings } from '../../services/settings/StaffSettingsProvider';
import { useEmployeeList } from '../../services/staff/useEmployeeList';
import { useReassignedShiftSummary } from '../../services/summary/useReassignedShiftSummary';
import {
  EmployeeAvailabilityExceptions,
  EmployeePattern,
  ReadEmployee,
  useGetRosteredStaffInDateRangeQuery,
  useLocationExceptionsQuery,
  useLocationWorkingPatternsQuery
} from '../gql/graphql.generated';
import { useSystemSettings } from '../settings/SystemSettingsProvider';
import { useTasks } from '../tasks/useTasks';
import { useLocation, useMyLocation } from '../useLocations';
import { buildEmployeeSummaryDayStats } from './buildEmployeeSummaryDayStats';
import { buildSummary } from './buildSummary';
import { SummaryEmployee } from './summary.types';

export const useSummary = (startDate: Date, endDate: Date, specifiedLocationId?: number) => {
  const selectedLocationId = useMyLocation().id;
  const locationId = specifiedLocationId || selectedLocationId;
  const {
    location: { name: locationName }
  } = useLocation(locationId);

  const { allEmployees: employees } = useEmployeeList({ date: startDate, specifiedLocation: specifiedLocationId });
  const { addReassignedShifts } = useReassignedShiftSummary(startDate, endDate);

  // Fetch a day outside of the requested range.
  // We'll need these for ghost shifts.
  const startDateToFetch = useMemo(() => {
    const date = new Date(startDate);
    date.setUTCDate(0);
    return date;
  }, [startDate]);

  const endDateToFetch = useMemo(() => {
    const date = new Date(endDate);
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + 1);
    return date;
  }, [endDate]);

  const [{ data: rosterData }, refetchRosteredStaff] = useGetRosteredStaffInDateRangeQuery({
    variables: {
      startDate: dateToApiFormat(startDateToFetch),
      endDate: dateToApiFormat(endDateToFetch),
      locationId
    },
    requestPolicy: 'cache-and-network'
  });

  const [{ data: exceptionData }] = useLocationExceptionsQuery({
    variables: {
      locationId,
      startDate: dateToApiFormat(startDateToFetch),
      endDate: dateToApiFormat(endDateToFetch)
    },
    requestPolicy: 'cache-and-network'
  });

  const [{ data: locationWorkingPatternsData }] = useLocationWorkingPatternsQuery({
    variables: {
      locationId,
      date: dateToApiFormat(startDateToFetch)
    },
    requestPolicy: 'cache-and-network'
  });

  const dateRange = useMemo(() => getDatePeriod(startDate, endDate), [startDate, endDate]);
  const getContractName = useGetContractName();
  const getStaffSettings = useStaffSettings(state => state.settingsForStaffMember);
  const mapToLeaveTypeId = useMapToLeaveTypeId();
  const getDayOfWeekAndHoliday = useGetDayOfWeekAndHoliday(startDateToFetch);
  const tasks = useTasks();
  const includeBreaks = useSystemSettings(state => state.settings.includeBreaksInTotals);

  // Using ! is sufficient because suspense pauses execution until the queries are populated.
  const rosteredStaff = rosterData!.rosteredStaffInDateRange!;
  const allAvailabilityData = exceptionData!.locationAvailabilityExceptions?.employeeAvailabilityExceptions || [];
  const workingPatterns = locationWorkingPatternsData!.locationWorkingPatterns!.employeePatterns!;

  const summary = useMemo(() => {
    return buildSummary({
      employees: employees as ReadEmployee[],
      rosteredStaff,
      dateRange,
      allAvailabilityData: allAvailabilityData as EmployeeAvailabilityExceptions[],
      addReassignedShifts,
      locationName,
      locationNumber: selectedLocationId,
      getContractName,
      getStaffSettings,
      workingPatterns: workingPatterns as EmployeePattern[],
      getDayOfWeekAndHoliday,
      mapToLeaveTypeId,
      tasks,
      includeBreaks
    });
  }, [
    employees,
    rosteredStaff,
    dateRange,
    allAvailabilityData,
    addReassignedShifts,
    locationName,
    selectedLocationId,
    getContractName,
    getStaffSettings,
    workingPatterns,
    getDayOfWeekAndHoliday,
    mapToLeaveTypeId,
    tasks,
    includeBreaks
  ]);

  const refetch = useCallback(() => {
    refetchRosteredStaff({ requestPolicy: 'network-only' });
  }, [refetchRosteredStaff]);

  const homeEmployeesSummary = useMemo(() => {
    return summary.filter(employee => employee.details.homeLocationId === selectedLocationId);
  }, [summary, selectedLocationId]);

  return {
    homeEmployeesSummary,
    allEmployeesSummary: summary,
    refetch
  };
};

export const useSummaryDayStats = (summary: SummaryEmployee[], dateRange: Date[]) => {
  return useMemo(() => {
    return buildEmployeeSummaryDayStats(summary, dateRange);
  }, [summary, dateRange]);
};

export const useSummaryStatsForDay = (summary: SummaryEmployee[], date: Date) => {
  return useMemo(() => {
    const stats = buildEmployeeSummaryDayStats(summary, [date]);

    if (!stats || stats.length === 0) {
      throw Error('No stats provided for selected day.');
    }

    return stats[0];
  }, [summary, date]);
};
